import {
  requestsControllerGetRenewalHistory,
  vehicleLicensesControllerCreate,
  vehicleLicensesControllerDelete,
} from '@kalba/api-client';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { format, parseISO } from 'date-fns';
import {
  CalendarClock,
  CalendarDays,
  FileText,
  GitBranch,
  Layers,
  MessageSquare,
  MoreVertical,
  ScrollText,
  Tag,
  Trash2,
  UserCheck,
} from 'lucide-react';
import { useState } from 'react';
import { Button } from '../../../../components/ui/button';
import { DataTable, type DataTableColumn } from '../../../../components/ui/data-table';
import { FieldBox } from '../../../../components/ui/field-box';
import { DirhamIcon, Price } from '../../../../components/ui/price';
import type { PermitDetail, RenewalChainEntry } from './types';

interface Props {
  permit: PermitDetail;
  onUpdated: () => void;
}

const renewalColumns: DataTableColumn<RenewalChainEntry>[] = [
  {
    key: 'referenceNo',
    header: 'Reference No',
    render: (entry) =>
      entry.relation === 'current' ? (
        <span className="font-mono font-semibold text-gray-900">{entry.referenceNo}</span>
      ) : (
        <Link
          to="/permits/$permitId"
          params={{ permitId: entry.id }}
          className="font-mono text-green-700 underline hover:no-underline"
        >
          {entry.referenceNo}
        </Link>
      ),
  },
  {
    key: 'requestAction',
    header: 'Request Type',
    render: (entry) => <span className="text-gray-600">{entry.requestAction}</span>,
  },
  {
    key: 'status',
    header: 'Status',
    render: (entry) => <span className="text-gray-600">{entry.status}</span>,
  },
  {
    key: 'createdAt',
    header: 'Created',
    render: (entry) => (
      <span className="text-sm text-gray-400">
        {new Date(entry.createdAt).toLocaleDateString()}
      </span>
    ),
  },
];

// ── Main component ─────────────────────────────────────────────────────────

export function DetailsTab({ permit, onUpdated }: Props) {
  const [addLicenseOpen, setAddLicenseOpen] = useState(false);
  const [licenseNo, setLicenseNo] = useState('');
  const [placeOfIssue, setPlaceOfIssue] = useState('');

  const approvedRecord = permit.approvalRecords.find((r) => r.decision === 'APPROVED');

  const hasRenewalChain = !!(permit.isRenewalOfId || permit.renewals?.length);

  const { data: renewalChain } = useQuery({
    queryKey: ['renewal-history', permit.id],
    queryFn: () =>
      requestsControllerGetRenewalHistory({
        id: permit.id,
      }) as unknown as Promise<RenewalChainEntry[]>,
    enabled: hasRenewalChain,
  });

  const addLicense = useMutation({
    mutationFn: () =>
      vehicleLicensesControllerCreate({
        permitId: permit.id,
        requestBody: { licenseNumber: licenseNo, placeOfIssue },
      }),
    onSuccess: () => {
      onUpdated();
      setAddLicenseOpen(false);
      setLicenseNo('');
      setPlaceOfIssue('');
    },
  });

  const deleteLicense = useMutation({
    mutationFn: (licId: string) => vehicleLicensesControllerDelete({ permitId: permit.id, licId }),
    onSuccess: onUpdated,
  });

  return (
    <div className="space-y-6">
      {/* ── Permit Information & Additional Details card ───────────────────────────────── */}
      <div className="rounded-2xl border border-gray-200 shadow-sm p-8 bg-white">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900">Permit Information</h2>
          <button
            type="button"
            className="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
          >
            <MoreVertical size={20} />
          </button>
        </div>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <FieldBox icon={<FileText size={16} />} label="Permit Type">
            {permit.requestTypeConfig.name}
          </FieldBox>
          <FieldBox icon={<GitBranch size={16} />} label="Request Type">
            {permit.requestAction}
          </FieldBox>
          {permit.assignedInspector && (
            <FieldBox icon={<UserCheck size={16} />} label="Assigned Inspector">
              {permit.assignedInspector.name}
            </FieldBox>
          )}
          {permit.totalFee && (
            <FieldBox icon={<DirhamIcon className="w-4 h-4" />} label="Total Fee">
              <Price value={permit.totalFee} />
            </FieldBox>
          )}
          {permit.permissionDate && (
            <FieldBox icon={<CalendarDays size={16} />} label="Permission Date">
              {format(parseISO(permit.permissionDate), 'dd MMM yyyy')}
            </FieldBox>
          )}
          {permit.expiryDate && (
            <FieldBox icon={<CalendarClock size={16} />} label="Expiry Date">
              {format(parseISO(permit.expiryDate), 'dd MMM yyyy')}
            </FieldBox>
          )}
          {permit.licenseType && (
            <FieldBox icon={<Tag size={16} />} label="License Type">
              {permit.licenseType}
            </FieldBox>
          )}
          {permit.permitActivity && (
            <FieldBox icon={<ScrollText size={16} />} label="Permit Activity">
              {permit.permitActivity}
            </FieldBox>
          )}
          {permit.wasteClassification && (
            <FieldBox icon={<Layers size={16} />} label="Waste Classification">
              {permit.wasteClassification}
            </FieldBox>
          )}
          {permit.wasteType && (
            <FieldBox icon={<FileText size={16} />} label="Type of Waste">
              {permit.wasteType}
            </FieldBox>
          )}
          {permit.wasteQuantity && (
            <FieldBox icon={<Tag size={16} />} label="Quantity of Waste">
              {permit.wasteQuantity}
            </FieldBox>
          )}
          {permit.landfillLocation && (
            <FieldBox icon={<CalendarDays size={16} />} label="Landfill Location">
              {permit.landfillLocation}
            </FieldBox>
          )}
          {approvedRecord && (
            <FieldBox icon={<UserCheck size={16} />} label="Approved By">
              {approvedRecord.reviewedBy.name}
            </FieldBox>
          )}
        </div>

        {permit.envPermitGeneralComments && (
          <div className="mt-6 pt-6 border-t border-gray-200">
            <div className="flex items-center gap-2 text-sm font-medium text-gray-400 mb-2">
              <MessageSquare size={16} />
              <span>General Comments</span>
            </div>
            <p className="text-sm text-gray-700 leading-relaxed">
              {permit.envPermitGeneralComments}
            </p>
          </div>
        )}

        {permit.facilitySubType && (
          <div className="mt-8 pt-8 border-t border-gray-200">
            <FieldBox icon={<Layers size={16} />} label="Facility Sub-Type">
              {permit.facilitySubType}
            </FieldBox>
          </div>
        )}
      </div>

      {/* ── Renewal history ───────────────────────────────────────── */}
      {hasRenewalChain && renewalChain && renewalChain.length > 1 && (
        <DataTable
          title="Renewal History"
          columns={renewalColumns}
          data={renewalChain}
          keyField="id"
        />
      )}

      {/* ── Vehicle licenses ──────────────────────────────────────── */}
      {permit.requestTypeConfig.code === 'VEH' && (
        <DataTable
          title="Vehicle Licenses"
          headerActions={
            permit.status === 'DRAFT' ? (
              <Button variant="primary" size="sm" onClick={() => setAddLicenseOpen(true)}>
                + Add
              </Button>
            ) : undefined
          }
          columns={[
            {
              key: 'licenseNumber',
              header: 'License Number',
              render: (lic) => <span className="font-mono text-gray-900">{lic.licenseNumber}</span>,
            },
            {
              key: 'placeOfIssue',
              header: 'Place of Issue',
              render: (lic) => <span className="text-gray-600">{lic.placeOfIssue}</span>,
            },
            {
              key: 'createdAt',
              header: 'Added',
              render: (lic) => (
                <span className="text-sm text-gray-400">
                  {new Date(lic.createdAt).toLocaleDateString()}
                </span>
              ),
            },
            ...(permit.status === 'DRAFT'
              ? [
                  {
                    key: 'delete',
                    header: '',
                    render: (lic: (typeof permit.vehicleLicenses)[0]) => (
                      <button
                        type="button"
                        onClick={() => deleteLicense.mutate(lic.id)}
                        disabled={deleteLicense.isPending}
                        className="rounded p-1 text-red-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
                        title="Delete"
                      >
                        <Trash2 size={14} />
                      </button>
                    ),
                  } satisfies DataTableColumn<(typeof permit.vehicleLicenses)[0]>,
                ]
              : []),
          ]}
          data={permit.vehicleLicenses}
          keyField="id"
          emptyMessage="No vehicle licenses added"
          addRow={
            addLicenseOpen ? (
              <tr>
                <td className="px-4 py-3">
                  <input
                    className="flex h-9 w-full rounded-xl border border-gray-300 bg-white px-3.5 py-1 text-sm text-gray-900 placeholder-gray-400 hover:border-gray-400 focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-100 transition-all"
                    placeholder="License Number"
                    value={licenseNo}
                    onChange={(e) => setLicenseNo(e.target.value)}
                  />
                </td>
                <td className="px-4 py-3">
                  <input
                    className="flex h-9 w-full rounded-xl border border-gray-300 bg-white px-3.5 py-1 text-sm text-gray-900 placeholder-gray-400 hover:border-gray-400 focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-100 transition-all"
                    placeholder="Place of Issue"
                    value={placeOfIssue}
                    onChange={(e) => setPlaceOfIssue(e.target.value)}
                  />
                </td>
                <td />
                <td className="px-4 py-3">
                  <div className="flex gap-2">
                    <Button
                      variant="primary"
                      size="sm"
                      disabled={!licenseNo.trim() || !placeOfIssue.trim() || addLicense.isPending}
                      onClick={() => addLicense.mutate()}
                    >
                      Add
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setAddLicenseOpen(false)}>
                      Cancel
                    </Button>
                  </div>
                </td>
              </tr>
            ) : undefined
          }
        />
      )}
    </div>
  );
}
